import { TestimonialCardStyleKit, LayoutTypes } from "./types";

export type VariantId =
  | "minimal"
  | "luxury"
  | "modern"
  | "gradient"
  | "glass"
  | "vintage"
  | "clean"
  | "elegant"
  | "cyber"
  | "quantum"
  | "neotech";

export const testimonialCardVariants: Record<
  VariantId,
  TestimonialCardStyleKit
> = {
  minimal: {
    card: {
      outlineWidth: "0px",
      outlineColor: "transparent",
      padding: "1rem",
      borderRadius: "12px",
      shadow: "0 1px 3px rgba(0,0,0,0.1)",
      mode: "light",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 72,
      borderRadius: "8px",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 16,
      color: "#18181b",
      fontWeight: "600",
      fontSize: "1rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#71717a",
      fontWeight: "400",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#27272a",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.6,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#a1a1aa",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#18181b",
      outlineWidth: "0px",
      outlineColor: "transparent",
      size: "16px",
    },
  },

  luxury: {
    card: {
      outlineWidth: "1px",
      outlineColor: "#2d2d2d",
      padding: "1rem",
      borderRadius: "16px",
      shadow: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
      mode: "dark",
      layout: LayoutTypes.CENTERED,
    },
    avatar: {
      size: 72,
      borderRadius: "12px",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 18,
      color: "#e5e7eb",
      fontWeight: "700",
      fontSize: "1.125rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#9ca3af",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#d1d5db",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.8,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#6b7280",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#e5e7eb",
      outlineWidth: "1px",
      outlineColor: "#2d2d2d",
      size: "18px",
    },
  },

  modern: {
    card: {
      outlineWidth: "0px",
      outlineColor: "transparent",
      padding: "1rem",
      borderRadius: "16px",
      shadow: "0 20px 40px -15px rgba(0,0,0,0.1)",
      mode: "light",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 72,
      borderRadius: "50%",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 18,
      color: "#111827",
      fontWeight: "600",
      fontSize: "1.125rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#6b7280",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#374151",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.7,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#9ca3af",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#111827",
      outlineWidth: "0px",
      outlineColor: "transparent",
      size: "18px",
    },
  },

  gradient: {
    card: {
      outlineWidth: "0px",
      outlineColor: "transparent",
      padding: "2.5rem",
      borderRadius: "20px",
      shadow: "0 20px 40px -15px rgba(0,0,0,0.2)",
      mode: "light",
      layout: LayoutTypes.CENTERED,
    },
    avatar: {
      size: 60,
      borderRadius: "50%",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 20,
      color: "#000",
      fontWeight: "700",
      fontSize: "1.25rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#000",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#000",
      fontWeight: "500",
      fontSize: "1rem",
      lineHeight: 1.8,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#6366f1",
      fontWeight: "500",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#000",
      outlineWidth: "0px",
      outlineColor: "transparent",
      size: "20px",
    },
  },

  glass: {
    card: {
      outlineWidth: "1px",
      outlineColor: "rgba(255, 255, 255, 0.2)",
      padding: "1rem",
      borderRadius: "24px",
      shadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
      mode: "light",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 64,
      borderRadius: "16px",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 18,
      color: "#0f172a",
      fontWeight: "600",
      fontSize: "1.125rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#475569",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#334155",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.8,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#64748b",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#0f172a",
      outlineWidth: "1px",
      outlineColor: "rgba(255, 255, 255, 0.2)",
      size: "18px",
    },
  },

  vintage: {
    card: {
      outlineWidth: "2px",
      outlineColor: "#000",
      padding: "1rem",
      borderRadius: "4px",
      shadow: "5px 10px 0 #000",
      mode: "light",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 80,
      borderRadius: "4px",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 20,
      color: "#000",
      fontWeight: "700",
      fontSize: "1.25rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#000",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#000",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.8,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#000",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#000",
      outlineWidth: "2px",
      outlineColor: "#000",
      size: "20px",
    },
  },

  clean: {
    card: {
      outlineWidth: "0px",
      outlineColor: "transparent",
      padding: "1rem",
      borderRadius: "8px",
      shadow: "0 2px 8px rgba(0,0,0,0.06)",
      mode: "light",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 52,
      borderRadius: "6px",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 17,
      color: "#1a1a1a",
      fontWeight: "600",
      fontSize: "1.0625rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#666666",
      fontWeight: "400",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 15,
      color: "#333333",
      fontWeight: "400",
      fontSize: "0.9375rem",
      lineHeight: 1.7,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#888888",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#1a1a1a",
      outlineWidth: "0px",
      outlineColor: "transparent",
      size: "17px",
    },
  },

  elegant: {
    card: {
      outlineWidth: "1px",
      outlineColor: "#e2e8f0",
      padding: "1rem",
      borderRadius: "14px",
      shadow: "0 10px 25px -5px rgba(0,0,0,0.08)",
      mode: "light",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 72,
      borderRadius: "50%",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 18,
      color: "#1e293b",
      fontWeight: "600",
      fontSize: "1.125rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#64748b",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#334155",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.75,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#94a3b8",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#1e293b",
      outlineWidth: "1px",
      outlineColor: "#e2e8f0",
      size: "18px",
    },
  },

  cyber: {
    card: {
      outlineWidth: "1px",
      outlineColor: "#1e293b",
      padding: "1rem",
      borderRadius: "4px",
      shadow: "0 0 30px rgba(30, 41, 59, 0.2)",
      mode: "dark",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 64,
      borderRadius: "4px",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 18,
      color: "#94a3b8",
      fontWeight: "600",
      fontSize: "1.125rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#64748b",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#e2e8f0",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.8,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#475569",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#94a3b8",
      outlineWidth: "1px",
      outlineColor: "#1e293b",
      size: "18px",
    },
  },

  quantum: {
    card: {
      outlineWidth: "2px",
      outlineColor: "#334155",
      padding: "1rem",
      borderRadius: "16px",
      shadow: "0 25px 50px -12px rgba(51, 65, 85, 0.25)",
      mode: "light",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 72,
      borderRadius: "50%",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 20,
      color: "#334155",
      fontWeight: "700",
      fontSize: "1.25rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#64748b",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#475569",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.9,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#94a3b8",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#334155",
      outlineWidth: "2px",
      outlineColor: "#334155",
      size: "20px",
    },
  },

  neotech: {
    card: {
      outlineWidth: "0px",
      outlineColor: "transparent",
      padding: "1rem",
      borderRadius: "20px",
      shadow: "0 20px 40px rgba(148, 163, 184, 0.15)",
      mode: "light",
      layout: LayoutTypes.STACK,
    },
    avatar: {
      size: 68,
      borderRadius: "14px",
      visibility: "visible",
      defaultAvatar: "/default-avatar.png",
    },
    name: {
      size: 19,
      color: "#0f172a",
      fontWeight: "600",
      fontSize: "1.188rem",
      fontFamily: "Roboto",
    },
    bio: {
      size: 14,
      color: "#64748b",
      fontWeight: "500",
      fontSize: "0.875rem",
      fontFamily: "Roboto",
    },
    content: {
      size: 16,
      color: "#334155",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: 1.85,
      fontFamily: "Roboto",
      display: "block",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    date: {
      size: 12,
      color: "#94a3b8",
      fontWeight: "400",
      fontSize: "0.75rem",
      visibility: true,
      fontFamily: "Roboto",
    },
    rating: {
      color: "#0f172a",
      outlineWidth: "0px",
      outlineColor: "transparent",
      size: "19px",
    },
  },
};
